"use client";

import { Drawer } from "@base-ui/react/drawer";
import { X } from "lucide-react";
import { useLayoutEffect, useRef, useState, type ComponentProps, type ReactNode } from "react";

import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils/cn";

interface BottomSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Accessible name of the × button. */
  closeLabel: string;
  /** Hidden while the sheet is locked mid-action. */
  showCloseButton?: boolean;
  finalFocus?: ComponentProps<typeof Drawer.Popup>["finalFocus"];
  /** Rendered inside `Drawer.Content`; use `Drawer.Title` / `Drawer.Description` in it. */
  children: ReactNode;
}

/**
 * The centred, swipe-down bottom sheet the purchase flow's popups share: the
 * configurator's "Tillagt i varukorgen" and the cart's phone-sticker offer.
 * Content is centred in a column; the caller brings title, copy and actions.
 * When the content changes size (the added-to-cart popup stepping into the
 * phone offer, an error line appearing) the sheet eases to its new height
 * rather than jumping.
 */
export function BottomSheet({
  open,
  onOpenChange,
  closeLabel,
  showCloseButton = true,
  finalFocus,
  children,
}: BottomSheetProps) {
  return (
    <Drawer.Root open={open} onOpenChange={onOpenChange} swipeDirection="down">
      <Drawer.Portal>
        <Drawer.Backdrop className="fixed inset-0 z-40 min-h-dvh bg-ink/40 transition-opacity duration-[450ms] data-[ending-style]:opacity-0 data-[starting-style]:opacity-0" />
        <Drawer.Viewport className="fixed inset-0 z-50 flex flex-col justify-end">
          <Drawer.Popup
            finalFocus={finalFocus}
            className="relative mx-auto flex max-h-[90dvh] w-full max-w-md flex-col rounded-t-[1.75rem] border border-b-0 border-paper-3 bg-paper text-ink shadow-[0_-20px_40px_-10px_rgba(30,26,25,0.25)] outline-none [transform:translateY(var(--drawer-swipe-movement-y))] transition-transform duration-[450ms] ease-[cubic-bezier(0.32,0.72,0,1)] data-[ending-style]:[transform:translateY(100%)] data-[starting-style]:[transform:translateY(100%)] data-[swiping]:select-none data-[swiping]:duration-0"
          >
            <span aria-hidden className="mx-auto mt-3 h-1 w-11 shrink-0 rounded-full bg-ink/20" />
            {showCloseButton ? (
              <Drawer.Close
                render={
                  <Button
                    variant="ghost"
                    size="icon-sm"
                    className="absolute top-3 right-3"
                    aria-label={closeLabel}
                  />
                }
              >
                <X aria-hidden="true" />
              </Drawer.Close>
            ) : null}
            <AnimatedHeight>{children}</AnimatedHeight>
          </Drawer.Popup>
        </Drawer.Viewport>
      </Drawer.Portal>
    </Drawer.Root>
  );
}

/**
 * Pins the sheet body to its content's measured height, so a change in size
 * is a CSS transition — on the sheet's own curve — instead of a jump.
 * `Drawer.Content` stays the scroll container, which is what the drawer's
 * swipe-versus-scroll handling looks for; it only stops scrolling while the
 * height animates, so no scrollbar flashes up as the sheet grows.
 */
function AnimatedHeight({ children }: { children: ReactNode }) {
  const contentRef = useRef<HTMLDivElement>(null);
  // `null` until measured: the first paint is the content's own height.
  const [height, setHeight] = useState<number | null>(null);
  const [animating, setAnimating] = useState(false);

  useLayoutEffect(() => {
    const element = contentRef.current;
    if (!element) {
      return;
    }
    let measured: number | null = null;
    const observer = new ResizeObserver(([entry]) => {
      const next = entry.borderBoxSize[0]?.blockSize ?? element.offsetHeight;
      if (next === measured) {
        return;
      }
      // No transition under reduced motion, so nothing would end it.
      if (measured !== null && !window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
        setAnimating(true);
      }
      measured = next;
      setHeight(next);
    });
    observer.observe(element);
    return () => observer.disconnect();
  }, []);

  return (
    <div
      style={height === null ? undefined : { height }}
      onTransitionEnd={(event) => {
        if (event.target === event.currentTarget && event.propertyName === "height") {
          setAnimating(false);
        }
      }}
      onTransitionCancel={(event) => {
        if (event.target === event.currentTarget && event.propertyName === "height") {
          setAnimating(false);
        }
      }}
      className="min-h-0 transition-[height] duration-[450ms] ease-[cubic-bezier(0.32,0.72,0,1)] motion-reduce:transition-none"
    >
      <Drawer.Content className={cn("h-full", animating ? "overflow-hidden" : "overflow-y-auto")}>
        <div
          ref={contentRef}
          className="flex flex-col items-center gap-5 px-6 pt-4 pb-[max(1.5rem,env(safe-area-inset-bottom))] text-center"
        >
          {children}
        </div>
      </Drawer.Content>
    </div>
  );
}
